import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { IEmergencyUtilitiesChecklist } from '../../../shared/models/emergencyUtilitiesChecklist';
import { EmergencyUtilitiesChecklistService } from '../../../shared/services/emergencyUtilitiesChecklist.service';

@Component({
  selector: 'emergency-utilities-checklist',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './emergency-utilities-checklist.component.html',
  styleUrl: './emergency-utilities-checklist.component.css'
})
export class EmergencyUtilitiesChecklistComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  data: IEmergencyUtilitiesChecklist = {};

  // Utilities checklist rows: label + the Location / Responsibility field keys.
  utilityRows: { label: string; location: keyof IEmergencyUtilitiesChecklist; responsibility: keyof IEmergencyUtilitiesChecklist }[] = [
    { label: 'Generator', location: 'generator_location', responsibility: 'generator_responsibility' },
    { label: 'Alarm System', location: 'alarm_system_location', responsibility: 'alarm_system_responsibility' },
    { label: 'Sprinkler Connection', location: 'sprinkler_connection_location', responsibility: 'sprinkler_connection_responsibility' },
    { label: 'Emergency Equipment', location: 'emergency_equipment_location', responsibility: 'emergency_equipment_responsibility' },
    { label: 'Hazardous Materials', location: 'hazardous_materials_location', responsibility: 'hazardous_materials_responsibility' },
    { label: 'Gas – Master Valves/Switches/Shut-off', location: 'gas_master_valves_location', responsibility: 'gas_master_valves_responsibility' },
    { label: 'Power – Master Valves/Switches/Shut-off', location: 'power_master_valves_location', responsibility: 'power_master_valves_responsibility' },
    { label: 'Water – Master Valves/Switches/Shut-off', location: 'water_master_valves_location', responsibility: 'water_master_valves_responsibility' },
  ];

  // Providers/Suppliers rows.
  providerRows: { label: string; key: keyof IEmergencyUtilitiesChecklist }[] = [
    { label: 'Gas', key: 'gas_provider' },
    { label: 'Power', key: 'power_provider' },
    { label: 'Water', key: 'water_provider' },
  ];

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private userDataService: UserDataService,
    private checklistService: EmergencyUtilitiesChecklistService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadData();
  }

  loadData(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.checklistService.getChecklist(this.currentBuildingId).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) this.data = response.data || {};
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  selectYesNo(value: string): void {
    if (!this.canSave()) return;
    this.data.custodians_know_locations = value;
  }

  onSubmit(): void {
    if (!this.currentBuildingId) return;
    this.loading = true;
    this.checklistService.updateChecklist(this.currentBuildingId, this.data).subscribe({
      next: (response) => {
        this.loading = false;
        if (!response.has_error) {
          this.messageModal = 'success';
          this.popupMessage = 'This occurred after updating the Emergency Utilities Checklist.';
          this.loadData();
        } else {
          this.messageModal = 'unsuccess';
          this.popupMessage = response.message;
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.messageModal = 'unsuccess';
        this.popupMessage = error.message;
      }
    });
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
}
